import React, { useState } from "react";
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Typography,
  Box,
  TablePagination,
  Tooltip,
} from "@mui/material";
import {
  MoreVert,
  Edit,
  Delete,
  Lock,
  LockOpen,
  AdminPanelSettings,
  Visibility,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { toggleUserStatusAction } from "../../redux/slices/userManagementSlice";
import { USER_ROLES } from "../../utils/roleUtils";
import { SPACING, ICON_SIZES } from "../../theme/constants";

const UsersTable = ({
  users,
  pagination,
  onDeleteUser,
  onEditUser,
  onViewUser,
  onPageChange,
  onLimitChange,
  currentUser,
}) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleView = () => {
    if (selectedUser && onViewUser) {
      onViewUser(selectedUser);
    }
    handleMenuClose();
  };

  const handleEdit = () => {
    if (selectedUser && onEditUser) {
      onEditUser(selectedUser);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedUser) {
      onDeleteUser(selectedUser.id);
    }
    handleMenuClose();
  };

  const handleToggleStatus = async () => {
    if (selectedUser) {
      const newStatus = !selectedUser.isActive;
      await dispatch(
        toggleUserStatusAction({
          userId: selectedUser.id,
          isActive: newStatus,
        }),
      );
    }
    handleMenuClose();
  };

  const getRoleColor = (role) => {
    switch (role) {
      case USER_ROLES.ADMIN:
        return "primary";
      default:
        return "default";
    }
  };

  const getRoleIcon = (role) => {
    if (role === USER_ROLES.ADMIN) {
      return <AdminPanelSettings sx={{ fontSize: ICON_SIZES.xs, mr: 0.5 }} />;
    }
    return null;
  };

  const getInitials = (user) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user.name) {
      const parts = user.name.split(" ");
      return parts.length > 1
        ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
        : user.name.substring(0, 2).toUpperCase();
    }
    return user.email.substring(0, 2).toUpperCase();
  };

  const handleChangePage = (event, newPage) => {
    onPageChange(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    onLimitChange(parseInt(event.target.value, 10));
  };

  if (!users || users.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No users found
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="center">Sites</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: SPACING.sm,
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: user.isActive
                              ? "primary.main"
                              : "grey.400",
                          }}
                        >
                          {getInitials(user)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {user.name || `${user.firstName} ${user.lastName}`}
                          </Typography>
                          {user.emailVerified ? (
                            <Chip
                              label="Verified"
                              size="small"
                              color="success"
                              sx={{ height: 16, fontSize: 10, mt: 0.5 }}
                            />
                          ) : (
                            <Chip
                              label="Unverified"
                              size="small"
                              sx={{ height: 16, fontSize: 10, mt: 0.5 }}
                            />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{user.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getRoleIcon(user.role)}
                        label={user.role.replace("_", " ")}
                        size="small"
                        color={getRoleColor(user.role)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {user.sitesCount || 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={user.isActive ? "Active" : "Inactive"}
                        size="small"
                        color={user.isActive ? "success" : "default"}
                        icon={user.isActive ? <LockOpen /> : <Lock />}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.lastLoginAt
                          ? new Date(user.lastLoginAt).toLocaleDateString()
                          : "Never"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Actions">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, user)}
                          disabled={user.id === currentUser?.userId}
                        >
                          <MoreVert />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pagination?.total || 0}
            page={(pagination?.page || 1) - 1}
            onPageChange={handleChangePage}
            rowsPerPage={pagination?.limit || 10}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: 180,
          },
        }}
      >
        <MenuItem onClick={handleView}>
          <Visibility sx={{ mr: SPACING.sm, fontSize: ICON_SIZES.sm }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <Edit sx={{ mr: SPACING.sm, fontSize: ICON_SIZES.sm }} />
          Edit User
        </MenuItem>
        <MenuItem onClick={handleToggleStatus}>
          {selectedUser?.isActive ? (
            <>
              <Lock sx={{ mr: SPACING.sm, fontSize: ICON_SIZES.sm }} />
              Deactivate
            </>
          ) : (
            <>
              <LockOpen sx={{ mr: SPACING.sm, fontSize: ICON_SIZES.sm }} />
              Activate
            </>
          )}
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <Delete sx={{ mr: SPACING.sm, fontSize: ICON_SIZES.sm }} />
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};

export default UsersTable;
